import {Component, OnInit, ViewChildren, QueryList, ElementRef, ChangeDetectorRef, ViewChild} from '@angular/core';
import {Title} from '@angular/platform-browser';
import {FixedAssetReportsApiService, DepDetailFilters, DepDetailRowItem} from '../../../../Services/fixed-asset-reports-api.service';
import {FixedAssetModel} from '../../../../DataModels/fixed-asset-model';
import {DataExportService} from '../../../../Services/dataExport.service';
import {NotificationService} from '../../../../Services/notification.service';
import {EmailModel} from '../../../../DataModels/email-model';
import {Modal} from 'bootstrap';
import {DateFilterService} from '../../../../Services/dateFilter.service';
import {DataService} from '../../../../Services/data.service';
import {ResponseModel} from '../../../../DataModels/response-model';
import {ReportPrintService} from '../../../../Services/report-print.service';

@Component({
  selector: 'app-depreciation-schedule-report',
  templateUrl: './depreciation-schedule.component.html',
  styleUrls: ['./depreciation-schedule.component.css'],
  standalone: false
})
export class DepreciationScheduleReportComponent implements OnInit {
  assets: FixedAssetModel[] = [];
  // Map of MACRS category labels to useful life in months (mirrors Fixed Asset Form options)
  private macrsLifeByCategory = new Map<string, number>([
    ['3-year', 36],
    ['5-year', 60],
    ['7-year', 84],
    ['10-year', 120],
    ['15-year', 180],
    ['20-year', 240],
    ['27.5-year', 330],
    ['39-year', 468],
  ]);
  // VM required by report header buttons
  vm: any = {
    showFilters: false,
    filtered: false,
    buttonsDisabled: false,
    report: [],
    report_format: false,
    monthCount: 0,
    showSpecificDates: false,
    company: [],
    location: [],
    isLoaded: false,
    request: {
      fromDate: null,
      toDate: null,
      company_id: '',
      location_id: ''
    },
  };

  // Map MACRS category labels to useful life in months (mirror Fixed Asset Form options)
  private macrsMonthsMap: Record<string, number> = {
    '3-year': 36,
    '5-year': 60,
    '7-year': 84,
    '10-year': 120,
    '15-year': 180,
    '20-year': 240,
    '27.5-year': 330, // residential rental real property
    '39-year': 468,   // nonresidential real property
  };

  /** Return MACRS months for an asset category, or null if not a MACRS category */
  getMacrsMonths(category?: string): number | null {
    if (!category) {
      return null;
    }
    const months = this.macrsMonthsMap[String(category)] as number | undefined;
    return months && months > 0 ? months : null;
  }

  // NOTE: consolidate DI into a single constructor below

  // header button config like Trial Balance
  buttonConfig: any = {
    reportFormat: false,
    // use only one print button
    print: false,
    printReport: true,
    email: true,
    update: true,
    exportExcel: true,
    close: true,
  };
  @ViewChildren('tableRef') tables!: QueryList<ElementRef<HTMLTableElement>>;
  @ViewChild('printArea') printArea?: ElementRef<HTMLElement>;
  reportEmail: EmailModel = new EmailModel();
  // Localized labels for boolean display in template
  yesLabel: string = $localize`:Yes@@yes:Yes`;
  noLabel: string = $localize`:No@@no:No`;

  // Precomputed MACRS maps per asset id: { 'asset-id': { 'YYYY-MM': amount } }
  macrsByAssetId: Record<string, Record<string, number | null>> = {};
  // Accumulated MACRS maps per asset id: running sum by YYYY-MM
  macrsAccumByAssetId: Record<string, Record<string, number | null>> = {};

  // Account lookup for header display (Id -> {number,name})
  accountLookup: Record<string, { number: string; name: string }> = {};

  ymKey(d: string | Date): string {
    const dt = d instanceof Date ? d : new Date(d);
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
  }

  constructor(
    private title: Title,
    private reportsApi: FixedAssetReportsApiService,
    private dataExport: DataExportService,
    private notify: NotificationService,
    private cdr: ChangeDetectorRef,
    private dateFilter: DateFilterService,
    private tableDataService: DataService,
    private reportPrint: ReportPrintService,
  ) {
  }

  ngOnInit(): void {
    this.title.setTitle($localize`:Depreciation Schedule@@depSchedule:Depreciation Schedule`);
    this.vm.isLoaded = false;
    // initialize from/to date lookups similar to Trial Balance
    try {
      const ret = this.dateFilter.DateFilterInit(this.vm.request.toDate, this.vm.request.fromDate);
      this.vm.fromDateSelect = ret.fromDateSelect;
      this.vm.toDateSelect = ret.toDateSelect;
      this.vm.monthCount = ret.monthCount;
    } catch {
    }
    // Populate Company and Location lookups
    try {
      this.tableDataService.getData('company?state=active&pageNumber=0&pageSize=999999')
        .subscribe((result: ResponseModel) => {
          (this.vm as any).company = (result as any)?.Data ?? result;
        });
      this.tableDataService.getData('location?state=active&pageNumber=0&pageSize=999999')
        .subscribe((result: ResponseModel) => {
          (this.vm as any).location = (result as any)?.Data ?? result;
        });
      // Load accounts to render Numbers in header
      this.tableDataService.getData('account?state=active&pageNumber=0&pageSize=999999')
        .subscribe((result: ResponseModel) => {
          const accounts = (result as any)?.Data ?? (result as any);
          const map: Record<string, { number: string; name: string }> = {};
          (accounts as any[]).forEach((a: any) => {
            if (a && a.id != null) {
              map[String(a.id)] = {number: String(a.number ?? ''), name: String(a.name ?? '')};
            }
          });
          this.accountLookup = map;
        });
    } catch {
    }
    // Load report data from API (no mock, no IndexedDB)
    this.fetchSchedule();
  }

  // Header actions
  update(): void {
    this.vm.filtered = true;
    this.fetchSchedule();
  }

  clear(): void {
    this.vm.filtered = false;
    this.vm.request = {fromDate: null, toDate: null, company_id: '', location_id: ''};
    this.fetchSchedule();
  }

  close(): void {
    // placeholder for consistency (no navigation here)
  }

  onPrint(): void {
    const reportName = $localize`:Depreciation Schedule@@depSchedule:Depreciation Schedule`;
    const rawHtml = this.printArea?.nativeElement?.outerHTML || '';
    const content = this.reportPrint.cleanContentHtml(rawHtml);
    const html = this.reportPrint.buildHtml({title: reportName, contentHtml: content, orientation: 'landscape'});
    this.reportPrint.printInIframe(html);
  }

  onExport(): void {
    const first = this.tables?.first?.nativeElement;
    if (first) {
      this.dataExport.exportToExcel(first, 'depreciation-schedule.xlsx', 'Dep Schedule');
    } else {
      this.notify.bsWarning($localize`:Nothing to export@@nothingToExport:Nothing to export`);
    }
  }

  onEmail(): void {
    const emailDocObj = new EmailModel();
    const reportName = $localize`:Depreciation Schedule@@depSchedule:Depreciation Schedule`;

    emailDocObj.print_orientation = '1';
    emailDocObj.convert_attachment_to = 'pdf';
    emailDocObj.fromEmail = '';
    emailDocObj.fromName = 'AcctXErp';
    emailDocObj.header_id = '';
    emailDocObj.toEmail = '';
    emailDocObj.body = $localize`:EmailBody@@emailBody:Please see the attached ${reportName}.` +
      '<br><br><br>Best regards,<br><br>AcctXErp<br><br>';
    emailDocObj.Subject = 'AcctXErp ' + reportName;

    try {
      this.cdr.detectChanges();
    } catch {
    }
    // Use the printable area HTML so headers + tables match print
    let contentHtml = '';
    try {
      contentHtml = this.printArea?.nativeElement?.outerHTML || '';
    } catch {
    }
    emailDocObj.pdfHtml = this.reportPrint.buildHtml({
      title: reportName,
      contentHtml: this.reportPrint.cleanContentHtml(contentHtml),
      orientation: 'landscape'
    });

    const now = new Date();
    const yyyy = String(now.getFullYear());
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    emailDocObj.FileName = `Depreciation_Schedule_${yyyy}${mm}${dd}.pdf`;
    emailDocObj.activity_message = reportName;
    this.reportEmail = emailDocObj;

    const element = document.getElementById('sendEmailModal') as HTMLElement;
    if (element) {
      const emailModal = new Modal(element);
      // Ensure buttons are re-enabled after modal closes
      try {
        element.addEventListener('hidden.bs.modal', () => {
          this.vm.buttonsDisabled = false;
          this.vm.report = this.assets && this.assets.length ? this.assets : [{}];
        }, {once: true});
      } catch {
      }
      emailModal.show();
    }
  }


  // --- Data fetch (API-only) ---
  private fetchSchedule(): void {
    const page = 1;
    const pageSize = 100000; // large page to match no-paging UI
    const f: DepDetailFilters = {
      fromDate: this.vm.request.fromDate,
      toDate: this.vm.request.toDate,
      companyId: this.vm.request.company_id,
      locationId: this.vm.request.location_id,
      search: undefined,
    };
    this.vm.isLoaded = false;
    this.reportsApi.getDepreciationDetail(page, pageSize, f).subscribe({
      next: ({items}) => {
        // Group rows by assetId into an assets array compatible with the template
        const groups = new Map<string, any>();
        const macrsMapByAsset: Record<string, Record<string, number | null>> = {};
        const macrsAccByAsset: Record<string, Record<string, number | null>> = {};
        const addRow = (g: any, r: DepDetailRowItem) => {
          const row = {
            periodStart: r.periodStart,
            periodEnd: r.periodEnd,
            // Use sequential period numbering for the report (start at 1)
            periodNumber: (g.schedule?.length || 0) + 1,
            depreciationAmount: r.bookDepreciation,
            accumulatedDepreciation: r.bookAccumulated,
            endingBookValue: r.endingNBV
          } as any;
          g.schedule.push(row);
          // Track last posted depreciation (latest period with a positive depreciation amount)
          try {
            const depAmt = Number(r.bookDepreciation);
            if (Number.isFinite(depAmt) && depAmt !== 0) {
              const currEnd = new Date(r.periodEnd as any);
              const prevEnd = g.lastPostedDepDate ? new Date(g.lastPostedDepDate as any) : null;
              if (!prevEnd || currEnd > prevEnd) {
                g.lastPostedDepDate = r.periodEnd;
                g.bookDepreciationToDate = depAmt;
              }
            }
          } catch {
          }
          // Fill MACRS map entries for template lookups
          const ym = this.ymKey(r.periodStart);
          const mm = macrsMapByAsset[g.id] || (macrsMapByAsset[g.id] = {});
          mm[ym] = r.macrsDepreciation == null ? null : Number(r.macrsDepreciation);
          const ma = macrsAccByAsset[g.id] || (macrsAccByAsset[g.id] = {});
          if (r.macrsAccumulated == null) {
            // compute running if API didn’t provide
            const prev = Object.keys(ma).length ? (Object.values(ma).pop() as number | null) : 0;
            const next = (prev || 0) + (Number(r.macrsDepreciation) || 0);
            ma[ym] = Number.isFinite(next) ? +next.toFixed(2) : null;
          } else {
            ma[ym] = Number(r.macrsAccumulated);
          }
        };
        for (const r of items || []) {
          const id = String(r.assetId || '');
          if (!groups.has(id)) {
            groups.set(id, {
              id,
              name: r.assetName || id,
              assetTag: r.assetTag,
              category: r.category,
              companyName: (r as any).companyName,
              locationName: (r as any).locationName,
              entityName: (r as any).entityName,
              depreciationMethod: (r as any).depreciationMethod,
              schedule: [] as any[],
              // Will be populated while adding rows
              lastPostedDepDate: undefined as string | undefined,
              bookDepreciationToDate: undefined as number | undefined,
            });
          }
          addRow(groups.get(id), r);
        }
        this.assets = Array.from(groups.values());
        this.macrsByAssetId = macrsMapByAsset;
        this.macrsAccumByAssetId = macrsAccByAsset;
        this.vm.report = this.assets && this.assets.length ? this.assets : [{}];
        this.vm.isLoaded = true;
        try {
          this.cdr.detectChanges();
        } catch {
        }
      },
      error: (err) => {
        // Interceptor will toast; keep UI usable
        try {
          console.log('[Dep Schedule][fetch error]', err);
        } catch {
        }
        this.assets = [];
        this.macrsByAssetId = {};
        this.macrsAccumByAssetId = {};
        this.vm.report = [];
        this.vm.isLoaded = true;
        try {
          this.cdr.detectChanges();
        } catch {
        }
      }
    });
  }

  // (intentionally left blank)
}
